import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { FxIcon, FxTemplateCard } from '@/components/fx'
import { useBlobUrl } from '@/hooks/useBlobUrl'
import { cn } from '@/lib/utils'
import type { DocumentCounts, Template } from '@/types/domain'
import type { Folder } from '@/lib/api/folders'

import { TemplateActions, TemplateMenuButton, type OpenDialog } from './TemplateActions'
import { cardState, relativeDate } from './overviewModel'

/**
 * One Musterformular, as a grid card and as a list row.
 *
 * Both are built around the same three facts — the name, the status line
 * from the counts, and when it last changed — so the two views cannot drift
 * apart in what they claim.
 */

/** Where a form opens. The cockpit replaces today's handoff screen here. */
export const templateHref = (id: string) => `/templates/${id}`

function StatusLine({
  template,
  counts,
  className,
}: {
  template: Template
  counts: DocumentCounts | undefined
  className?: string
}) {
  const state = cardState(template, counts)
  if (state.kind === 'loading') {
    // The counts request is still out. An empty, sized placeholder rather
    // than a guess: a line that says "analysiert" and corrects itself to
    // "unklar" a moment later is worse than one that waits.
    return <span className={cn('inline-block h-5 w-24 rounded-sm bg-fill', className)} />
  }
  return (
    <span className={cn('inline-flex items-center gap-1 text-small', state.tone, className)}>
      <FxIcon name={state.icon} size={18} />
      {state.label}
    </span>
  )
}

export function TemplateGridCard({
  template,
  counts,
  folders,
  bookmarked,
  onToggleBookmark,
}: {
  template: Template
  counts: DocumentCounts | undefined
  folders: Folder[]
  bookmarked: boolean
  onToggleBookmark: () => void
}) {
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<OpenDialog>(null)
  const { url } = useBlobUrl(template.imagePath || null)
  const state = cardState(template, counts)
  const to = templateHref(template.id)

  return (
    <div className="flex w-full flex-col overflow-hidden rounded-sm bg-surface shadow-raised">
      {/* `FxTemplateCard` renders a real `<a href>` for the name and stretches
          it over the card, which is what makes the card middle-clickable and
          shows its target in the status bar — but a plain anchor would reload
          the whole app. Catching the bubbled click here keeps the SPA
          navigation while leaving cmd/ctrl/shift-click to the browser. */}
      <FxTemplateCard
        className="w-full rounded-none shadow-none"
        name={template.title || 'Ohne Namen'}
        thumbnail={url ?? undefined}
        href={to}
        done={state.done}
        bookmarked={bookmarked}
        onToggleBookmark={onToggleBookmark}
        onClick={(event) => {
          if (event.defaultPrevented) return
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
          if (!(event.target as HTMLElement).closest('a')) return
          event.preventDefault()
          navigate(to)
        }}
      />
      <div className="flex items-center gap-2 py-2 pl-4 pr-2">
        <StatusLine template={template} counts={counts} />
        <span className="flex-1" />
        <span className="text-small text-text-muted">{relativeDate(template.updatedAt)}</span>
        <TemplateMenuButton name={template.title} onSelect={setDialog} />
      </div>
      <TemplateActions
        template={template}
        folders={folders}
        open={dialog}
        onOpenChange={setDialog}
      />
    </div>
  )
}

export function TemplateListRow({
  template,
  counts,
  folders,
  bookmarked,
  onToggleBookmark,
}: {
  template: Template
  counts: DocumentCounts | undefined
  folders: Folder[]
  bookmarked: boolean
  onToggleBookmark: () => void
}) {
  const [dialog, setDialog] = useState<OpenDialog>(null)

  return (
    <li className="relative flex min-h-list-row items-center gap-4 rounded-sm bg-surface px-4 py-2 shadow-raised">
      <FxIcon name="description" size={24} className="flex-none text-primary" filled />
      <Link
        to={templateHref(template.id)}
        className="min-w-0 flex-1 truncate text-body-strong text-text no-underline after:absolute after:inset-0 after:content-['']"
      >
        {template.title || 'Ohne Namen'}
      </Link>
      <StatusLine template={template} counts={counts} className="hidden xs:inline-flex" />
      <span className="hidden w-24 flex-none text-right text-small text-text-muted sm:inline">
        {relativeDate(template.updatedAt)}
      </span>
      <button
        type="button"
        className="relative z-10 flex flex-none rounded-sm p-1 text-primary"
        aria-label={
          bookmarked
            ? `„${template.title}“ aus Favoriten entfernen`
            : `„${template.title}“ zu Favoriten hinzufügen`
        }
        aria-pressed={bookmarked}
        onClick={onToggleBookmark}
      >
        <FxIcon name="bookmark" size={22} filled={bookmarked} />
      </button>
      <TemplateMenuButton name={template.title} onSelect={setDialog} />
      <TemplateActions
        template={template}
        folders={folders}
        open={dialog}
        onOpenChange={setDialog}
      />
    </li>
  )
}
